// ============================================
// CALENDAR CONFIG HELPERS
// ============================================
//
// t16: pure helpers around the calendar view's persisted config shape.
// Kept in their own module so calendar-view.tsx (state owner) and
// calendar-settings-panel.tsx (config editor) share one definition of
// "what counts as a valid date source / color rule / view mode".
//
// The shape is intentionally aligned with the backend's viewConfigSchema
// (backend/src/schemas/view.ts) so the saved view round-trip is loss-
// free.

import type {
    CustomField,
    Task,
    TaskStatus,
    TaskPriority,
    ViewConfig,
} from '@/lib/schemas';
import {
    DEFAULT_CALENDAR_DATE_SOURCE,
    DEFAULT_CALENDAR_VIEW_MODE,
    DEFAULT_CALENDAR_COLOR_BY,
    CALENDAR_DATE_SOURCES,
    CALENDAR_VIEW_MODES,
} from '@/lib/schemas';

// All built-in Task fields the Calendar view can show events for. The
// backend's dateSource enum also lists 'sprintStart' / 'sprintEnd' —
// those are handled separately because they resolve via the project's
// Sprint custom field rather than a Task column.
export type CalendarDateSource = (typeof CALENDAR_DATE_SOURCES)[number];
export type CalendarViewMode = (typeof CALENDAR_VIEW_MODES)[number];

// T21 R4: a user-defined date range rendered as a sprint-band-style
// highlight on the calendar grid. Persisted in the active view's
// config under `calendarSettings.periods`. `startDate` / `endDate` are
// always YYYY-MM-DD (date-only, no time component) so they can be
// compared directly against the grid's day keys without TZ math.
export interface PeriodDef {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    color?: string;
}

// T21 R4: special 'all' sentinel for the date source selector that
// means "show every task, ignore all date columns". Kept out of the
// shared `CALENDAR_DATE_SOURCES` constant on purpose — that constant
// is shared with the backend view schema and 'all' would fail server-
// side validation. The settings panel treats 'all' as a display-only
// prefix and the rest of the Calendar view handles it as
// "no date filter".
export const CALENDAR_DATE_SOURCE_ALL = 'all' as const;
export type CalendarDateSourceWithAll = CalendarDateSource | typeof CALENDAR_DATE_SOURCE_ALL;

// The active view's normalised calendar config — every field has a
// concrete default so downstream code never has to deal with `undefined`.
export interface NormalisedCalendarConfig {
    viewMode: CalendarViewMode;
    dateSource: CalendarDateSourceWithAll;
    colorBy: string;
    filters: ViewConfig['filters'];
    /** T21 R4: user-defined date ranges that render as bands. */
    periods: PeriodDef[];
}

// Read a SavedView's stored config into a NormalisedCalendarConfig.
// Unknown / missing values fall back to the same defaults
// `buildDefaultCalendarViewConfig()` uses on the backend.
export function normaliseCalendarConfig(config: ViewConfig | undefined | null): NormalisedCalendarConfig {
    const viewMode = isViewMode(config?.viewMode) ? config!.viewMode! : DEFAULT_CALENDAR_VIEW_MODE;
    const dateSource = isDateSource(config?.dateSource) ? config!.dateSource! : DEFAULT_CALENDAR_DATE_SOURCE;
    const colorBy = typeof config?.colorBy === 'string' && config.colorBy.length > 0
        ? config.colorBy
        : DEFAULT_CALENDAR_COLOR_BY;
    // T21 R4: read periods from any of the legacy keys the user might
    // have stored. We accept `periods`, `calendarSettings.periods`, or
    // the nested object shape so a stale view doesn't drop its bands
    // on the next save. (Defensive — the Settings panel only ever
    // writes the top-level `periods` key.)
    const raw = (config as any)?.periods;
    const periods = Array.isArray(raw)
        ? raw.filter(isPeriodDef)
        : [];
    return {
        viewMode,
        dateSource,
        colorBy,
        filters: config?.filters ?? {},
        periods,
    };
}

function isPeriodDef(v: unknown): v is PeriodDef {
    if (!v || typeof v !== 'object') return false;
    const p = v as any;
    return (
        typeof p.id === 'string' &&
        typeof p.name === 'string' &&
        typeof p.startDate === 'string' &&
        typeof p.endDate === 'string' &&
        (typeof p.color === 'string' || typeof p.color === 'undefined')
    );
}

function isViewMode(v: unknown): v is CalendarViewMode {
    return typeof v === 'string' && (CALENDAR_VIEW_MODES as readonly string[]).includes(v);
}

function isDateSource(v: unknown): v is CalendarDateSource {
    return typeof v === 'string' && (CALENDAR_DATE_SOURCES as readonly string[]).includes(v);
}

// ============================================
// DATE RESOLUTION
// ============================================
//
// Resolve the ISO date string a task should be placed on for the
// currently-selected dateSource. For sprintStart / sprintEnd we look up
// the Sprint custom field on the project and resolve against its
// `sprints[]` payload (see use-calendar.ts for the parsing helper).
//
// Returns null when the task has no usable date for the current source
// (caller skips rendering the task).

import type { SprintDef } from '@/hooks/use-calendar';

export interface TaskDateResolution {
    /**
     * The raw ISO datetime / date string from Prisma — NOT pre-sliced.
     * Consumers MUST derive their YYYY-MM-DD day-key via
     * `extractIsoDate(resolution.start)` to avoid the day-1 timezone
     * shift bug (Prisma stores `DateTime?` as UTC, and `new Date(iso)`
     * then converts to local TZ). The raw string is kept so consumers
     * that care about time-of-day (e.g. the Agenda view's HH:MM label)
     * can still read it from `start.includes('T') && start.length > 11`.
     */
    start: string | null;
    /** ISO datetime — end of the day (or null for a point-in-time task). */
    end: string | null;
    /** True when start != end (multi-day event). */
    isMultiDay: boolean;
    /**
     * For sprintStart / sprintEnd sources: the resolved sprint id, if any.
     * Used by the Calendar to colour the pill and by drag-to-update to
     * know which sprint to assign.
     */
    sprintId?: string;
}

export function resolveTaskDate(
    task: Task,
    dateSource: CalendarDateSourceWithAll,
    sprints: SprintDef[]
): TaskDateResolution {
    // The returned `start` / `end` are the *original* ISO strings so
    // downstream code can still detect a non-midnight time-of-day when
    // present. Consumers MUST derive their day-key with
    // `extractIsoDate(resolution.start)` to avoid the "day-1 bug"
    // (Prisma serialises `DateTime?` to UTC, and `new Date(iso)` then
    // shifts the day back in non-UTC timezones).
    //
    // T21 R4: 'all' bypasses the date-source filter and falls back to
    // ANY date on the task (createdAt → startDate → dueDate → endDate)
    // so every task lands somewhere on the calendar instead of being
    // hidden when its primary date column is empty.
    if (dateSource === CALENDAR_DATE_SOURCE_ALL) {
        const fallback =
            (task as any).createdAt ??
            (task as any).startDate ??
            task.dueDate ??
            (task as any).endDate ??
            null;
        return fallback
            ? { start: fallback, end: fallback, isMultiDay: false }
            : { start: null, end: null, isMultiDay: false };
    }

    if (dateSource === 'dueDate') {
        const iso = task.dueDate ?? null;
        return iso
            ? { start: iso, end: iso, isMultiDay: false }
            : { start: null, end: null, isMultiDay: false };
    }

    if (dateSource === 'startDate') {
        const iso = (task as any).startDate ?? null;
        const endIso = (task as any).endDate ?? null;
        return makeResolution(iso, endIso);
    }

    if (dateSource === 'endDate') {
        const iso = (task as any).endDate ?? null;
        return iso
            ? { start: iso, end: iso, isMultiDay: false }
            : { start: null, end: null, isMultiDay: false };
    }

    // sprintStart / sprintEnd — both rely on the Sprint custom field
    // already-resolved `sprints[]` payload. We read the task's sprint
    // selection by inspecting customFieldValues for any SPRINT-typed
    // field whose option id matches a sprint.
    const sprintField = findSprintField(task, sprints);
    if (!sprintField) return { start: null, end: null, isMultiDay: false };
    const sprint = sprints.find((s) => s.id === sprintField.sprintId);
    if (!sprint) return { start: null, end: null, isMultiDay: false, sprintId: sprintField.sprintId };

    if (dateSource === 'sprintStart') {
        return { start: sprint.startDate, end: sprint.startDate, isMultiDay: false, sprintId: sprint.id };
    }
    // sprintEnd
    return { start: sprint.endDate, end: sprint.endDate, isMultiDay: false, sprintId: sprint.id };
}

// Helper for startDate source — when both startDate and endDate are set
// the event spans multiple days. Inputs are assumed to already be
// YYYY-MM-DD (sliced by resolveTaskDate), so multi-day comparison is a
// plain string compare.
function makeResolution(startIso: string | null, endIso: string | null): TaskDateResolution {
    if (!startIso) return { start: null, end: null, isMultiDay: false };
    if (!endIso) return { start: startIso, end: startIso, isMultiDay: false };
    return {
        start: startIso,
        end: endIso,
        isMultiDay: startIso !== endIso,
    };
}

// ============================================
// YYYY-MM-DD SLICE
// ============================================
//
// Prisma stores `dueDate` / `startDate` / `endDate` as `DateTime?` which
// gets serialised to UTC ISO datetimes (e.g. "2026-09-15T00:00:00.000Z").
// If we then feed that into the local timezone via `new Date(iso)` the
// date can shift back one day for any user east of UTC — the "day-1 bug".
//
// The fix is to never go through `new Date()` for the *calendar grid
// key*: just take the YYYY-MM-DD slice at the start of the string. ISO
// 8601 dates always begin with that prefix and are timezone-agnostic
// (the slice doesn't know what timezone the server stored the value in).
//
// Falls back to UTC parsing only for non-ISO inputs (defensive: custom
// code paths might pass a locale-formatted string). Returns null when
// the input is null/undefined or not parseable.

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

export function extractIsoDate(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const s = String(iso);
    if (ISO_DATE_PREFIX.test(s)) return s.slice(0, 10);
    // Defensive fallback — parse as Date and render in UTC so the day
    // never shifts regardless of the browser's local TZ. This is for
    // non-ISO inputs only; the canonical path is the slice above.
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// ============================================
// SPRINT-FIELD LOOKUP FOR A TASK
// ============================================
//
// Tasks store their sprint selection as `task.customFieldValues[<id>] =
// <sprintId>`. We don't have the field's id handed in here so we walk
// every customFieldValue and compare against the known sprint ids.
// Cheap (typical sprint counts <20) and avoids plumbing the field id
// through the date resolver.

function findSprintField(
    task: Task,
    sprints: SprintDef[]
): { fieldId: string; sprintId: string } | null {
    if (!task.customFieldValues) return null;
    const sprintIds = new Set(sprints.map((s) => s.id));
    for (const [fieldId, value] of Object.entries(task.customFieldValues)) {
        if (value == null) continue;
        const sid = typeof value === 'string' ? value : String(value);
        if (sprintIds.has(sid)) {
            return { fieldId, sprintId: sid };
        }
    }
    return null;
}

// ============================================
// COLOR RESOLUTION
// ============================================
//
// Pick the colour for a task's pill under the active colorBy rule. The
// rule can be a built-in ('status' | 'priority' | 'assignee') or a
// custom-field id. Sprint custom fields fall back to the sprint's own
// colour when the task is associated with a sprint.

export type CalendarColor =
    | { kind: 'status'; value: TaskStatus }
    | { kind: 'priority'; value: TaskPriority }
    | { kind: 'assignee'; value: string[] }
    | { kind: 'custom'; fieldId: string; value: unknown }
    | { kind: 'fixed'; hex: string };

const STATUS_PALETTE: Record<TaskStatus, string> = {
    TODO: '#6366f1', // indigo-500
    IN_PROGRESS: '#3b82f6', // blue-500
    IN_REVIEW: '#a855f7', // purple-500
    DONE: '#22c55e', // green-500
    CANCELLED: '#9ca3af', // gray-400
};

const PRIORITY_PALETTE: Record<TaskPriority, string> = {
    NONE: '#9ca3af',
    LOW: '#0ea5e9',
    MEDIUM: '#f59e0b',
    HIGH: '#f97316',
    URGENT: '#ef4444',
};

// Stable-ish hash so an assignee gets a consistent colour across renders.
// Not cryptographically interesting — just a deterministic mapping.
function hashStringToHue(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = (h << 5) - h + s.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h) % 360;
}

export function resolveCalendarColor(
    task: Task,
    colorBy: string,
    sprints: SprintDef[],
    sprintField: CustomField | null
): CalendarColor {
    if (colorBy === 'status') {
        return { kind: 'status', value: task.status };
    }
    if (colorBy === 'priority') {
        return { kind: 'priority', value: task.priority };
    }
    if (colorBy === 'assignee') {
        const ids = task.assignees.map((a) => a.userId);
        return { kind: 'assignee', value: ids };
    }
    if (sprintField && colorBy === sprintField.id) {
        // Sprint-coloured: prefer the resolved sprint's colour over the
        // generic custom-field value rendering.
        const sidRaw = task.customFieldValues?.[sprintField.id];
        const sid = typeof sidRaw === 'string' ? sidRaw : sidRaw == null ? '' : String(sidRaw);
        const sprint = sprints.find((s) => s.id === sid);
        if (sprint?.color) {
            return { kind: 'fixed', hex: sprint.color };
        }
        return { kind: 'custom', fieldId: sprintField.id, value: sid };
    }
    return { kind: 'custom', fieldId: colorBy, value: task.customFieldValues?.[colorBy] };
}

// Render a CalendarColor to a hex string. Sprint-coloured pills fall
// through to the sprint's stored colour; assignee colours use a hue
// derived from the first assignee id (so single-user lists stay
// consistent); everything else falls back to the active custom field's
// option colour, then to a neutral default.
export function colorToHex(
    color: CalendarColor,
    customFields: CustomField[]
): string {
    switch (color.kind) {
        case 'status':
            return STATUS_PALETTE[color.value] ?? '#6366f1';
        case 'priority':
            return PRIORITY_PALETTE[color.value] ?? '#9ca3af';
        case 'assignee': {
            if (color.value.length === 0) return '#9ca3af';
            const hue = hashStringToHue(color.value[0] ?? '');
            return `hsl(${hue}, 65%, 55%)`;
        }
        case 'custom': {
            const field = customFields.find((f) => f.id === color.fieldId);
            if (!field || !field.options) return '#6366f1';
            // SELECT / MULTI_SELECT — options are { id, label, color? }.
            const opts = Array.isArray(field.options) ? field.options : [];
            const v = color.value;
            if (typeof v === 'string') {
                const opt = opts.find((o) => o.id === v);
                if (opt?.color) return opt.color;
            } else if (Array.isArray(v)) {
                const first = opts.find((o) => v.includes(o.id));
                if (first?.color) return first.color;
            }
            return '#6366f1';
        }
        case 'fixed':
            return color.hex;
    }
}
